import axios, { type AxiosInstance } from "axios";

/**
 * One axios instance for the whole dashboard. The JWT lives in localStorage —
 * it is a short-lived admin token on an internal tool, and a 401 anywhere sends
 * the user straight back to the login screen.
 */

const TOKEN_KEY = "llm-logger.token";

// Dev goes through Vite's /api proxy; production talks to the API host directly
// (VITE_API_URL is baked at image build time).
const baseURL = import.meta.env.DEV ? "/api" : (import.meta.env.VITE_API_URL ?? "");

export const api: AxiosInstance = axios.create({
  baseURL,
  timeout: Number(import.meta.env.VITE_API_TIMEOUT ?? 60000),
});

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      setToken(null);
      onUnauthorized?.();
    }
    return Promise.reject(error);
  }
);

/** Human-readable message from an axios error, for the error states. */
export function errorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (error.code === "ECONNABORTED") return "요청 시간이 초과됐어요. 기간을 좁혀서 다시 시도해 주세요.";
    if (!error.response) return "수집 서버에 연결하지 못했어요. 잠시 후 다시 시도해 주세요.";
    return `요청이 실패했어요 (${error.response.status})`;
  }
  return "알 수 없는 오류가 발생했어요.";
}
